import { NOT_SCHEDULED_DATE } from "constants/order";
import { getAbnSelectionProcedures } from "helpers/abn";
import { parseDate } from "helpers/date";
import { ITourOrder } from "training/types";
import { IOverviewPage } from "types/Customize";
import { IOrder } from "types/Order/Order";

/**
 * CLE-3015 — the tour's view of the trainee's estimate, from GET /orders/:id. Pure; each field mirrors the
 * code that decides what the page shows (named per field), so the curriculum's conditions can read it
 * without the DOM.
 *
 * `pageContent` is the content the patient page renders from (Overview.tsx: GET
 * /customize/:orderType/account/:accountId → `overviewPage`), which falls back to the DEFAULT customization
 * when the account has no row of its own; `null` when that read found nothing. Pass it whenever it is known:
 * the order's `account.customizes` holds the account's own rows only, so without it a hospital on the
 * default customization read as "no expiry" while its page showed one (manual pass 2026-10-26, 4.3n).
 * Undefined → the account's own row, as before.
 */
export function toTourOrder(
  order: IOrder,
  pageContent?: IOverviewPage | null
): ITourOrder {
  const accountRow = order.account?.customizes?.find(
    (custom) => custom.orderType === order.orderType
  )?.overviewPage;
  // Home.tsx with no content at all: the discount shows (undefined counts as on), the expiry does not.
  const overviewPage =
    pageContent === undefined
      ? accountRow
      : pageContent ?? ({} as Partial<IOverviewPage>);
  const abnApplies = !!getAbnSelectionProcedures(order)?.length;
  return {
    id: order.id,
    externalId: order.externalId,
    status: order.status,
    orderType: order.orderType,
    // OrderView.tsx: the active plan.
    planEnabled: !!order.paymentPlan?.some((plan) => plan.active),
    paid: !!order.payments?.some((payment) => payment.status === "Paid"),
    // PatientDetails.tsx: "Send Custom Text" is hidden for an email-only patient.
    textsPatient: order.patient?.preferredContactMethod !== "Email",
    // Home.tsx: the "Not scheduled" sentinel date.
    scheduled: parseDate(order.dateOfService) !== NOT_SCHEDULED_DATE,
    abnApplies,
    // Home.tsx counts a non-void ABN on the order as signed (it also compares the codes; inferred enough
    // for the tour: a fresh estimate has none).
    abnSigned: !!order.abnNotices?.some(
      (notice) => !(notice as { isVoid?: boolean }).isVoid && !notice.isVoided
    ),
    // Home.tsx: the "Save N%" tag and "N% off" show unless the setting is explicitly off.
    discountShown: overviewPage
      ? overviewPage.discountPercentTotal !== false
      : undefined,
    discountExpiryShown: overviewPage
      ? !!overviewPage.discountExpires
      : undefined,
  };
}
